import { useEffect, useState } from 'react';

export default function useMaintenance(options = {}) {
  const {
    endpoint = `${process.env.REACT_APP_API_URL}/api/maintenance-status`,
    pollMs = 0, // 0 = no polling
    transform, // optional: map backend JSON to {maintenance:boolean, message?:string}
  } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState({ maintenance: false, message: '' });

  const force = String(process.env.REACT_APP_FORCE_MAINTENANCE || '').toLowerCase() === 'true';
  const overrideOff = typeof window !== 'undefined' && localStorage.getItem('maintenanceOverride') === 'off';

  async function fetchStatus(signal) {
    try {
      setLoading(true);
      let server = { maintenance: false, message: '' };
      if (endpoint) {
        const res = await fetch(endpoint, { signal });
        if (res.ok) {
          const json = await res.json();
          // Apply custom transform if provided
          if (typeof transform === 'function') {
            const t = transform(json);
            if (t && typeof t === 'object') {
              server = { maintenance: !!t.maintenance, message: t.message || '' };
            }
          } else {
            // Default inference
            if (Array.isArray(json)) {
              const first = json[0] || {};
              if (typeof first.maintenance !== 'undefined') {
                server = { maintenance: !!first.maintenance, message: first.message || '' };
              } else if (typeof first.site_status !== 'undefined') {
                const isMaint = String(first.site_status) === '0';
                server = { maintenance: isMaint, message: first.message || first.site_notice || '' };
              } else {
                server = { maintenance: false, message: '' };
              }
            } else if (json && typeof json === 'object') {
              if (typeof json.maintenance !== 'undefined') {
                server = { maintenance: !!json.maintenance, message: json.message || '' };
              } else if (typeof json.site_status !== 'undefined') {
                const isMaint = String(json.site_status) === '0';
                server = { maintenance: isMaint, message: json.message || json.site_notice || '' };
              } else {
                server = { maintenance: false, message: '' };
              }
            }
          }
        }
      }
      const maintenance = !overrideOff && (force || !!server.maintenance);
      setStatus({ maintenance, message: server.message || '' });
      setError(null);
    } catch (e) {
      const maintenance = !overrideOff && force;
      setStatus({ maintenance, message: '' });
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const ctrl = new AbortController();
    fetchStatus(ctrl.signal);

    if (pollMs > 0) {
      const id = setInterval(() => fetchStatus(ctrl.signal), pollMs);
      return () => { clearInterval(id); ctrl.abort(); };
    }
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pollMs]);

  return { ...status, loading, error };
}
